import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useThrottle } from '~/_shared/utils/hooks/useThrottle';
import { useMap } from '~/map/map/mapContext';
import { useProjectionOverlay } from '~/map/map/useProjectionOverlay';
import type { MapObjectManager } from '../../private/mapObjectManager';
import type { ShapeSubmitOption } from '../drawMapShape.container';
import { useDrawMapShapeContext } from './drawMapShapeContext';

const MIN_DOTS_DISTANCE = 15; // pixels
const THROTTLE_MS = 150;

export type UseFreeformMapShapeDrawProps = {
  active: boolean;
  manager: MapObjectManager;
  onDone?: (how: ShapeSubmitOption) => void;
};

export const useFreeformMapShapeDraw = ({
  active,
  manager,
  onDone,
}: UseFreeformMapShapeDrawProps) => {
  const { shapeRef, addOutline } = useDrawMapShapeContext();
  const map = useMap();
  const { getDistanceBetweenPoints } = useProjectionOverlay(map);

  const [isStarted, setIsStarted] = useState(false);

  // Refs to avoid stale closures in the mouse move handler
  const isStartedRef = useRef(isStarted);
  isStartedRef.current = isStarted;

  const addOutlineRef = useRef(addOutline);
  addOutlineRef.current = addOutline;

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const getDistanceBetweenPointsRef = useRef(getDistanceBetweenPoints);
  getDistanceBetweenPointsRef.current = getDistanceBetweenPoints;

  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!active) {
      setIsStarted(false);
    }
  }, [active]);

  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!activeRef.current || !event.latLng) {
      return;
    }

    const latLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };

    if (!isStartedRef.current) {
      setIsStarted(true);
      addOutlineRef.current(latLng);
      return;
    }

    onDoneRef.current?.('DoubleClick');
  }, []);

  useEffect(() => {
    if (!active) {
      return;
    }
    const clickListener = manager.addMapClickListener(onMapClick);
    return () => {
      clickListener.remove();
    };
  }, [active, onMapClick, manager]);

  const onMouseMove = useThrottle((event: google.maps.MapMouseEvent) => {
    if (!activeRef.current || !isStartedRef.current || !event.latLng) {
      return;
    }

    const currentShape = shapeRef.current;
    if (!currentShape || !currentShape.outlines.length) {
      return;
    }

    const latLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    const lastOutline = currentShape.outlines.lastItem;
    const pixelDistance = getDistanceBetweenPointsRef.current(lastOutline, latLng);

    if (pixelDistance === null || pixelDistance < MIN_DOTS_DISTANCE) {
      return;
    }

    addOutlineRef.current(latLng);
  }, [shapeRef], THROTTLE_MS);

  useEffect(() => {
    if (!active) {
      return;
    }
    const moveListener = manager.addMapMouseMoveListener(onMouseMove);
    return () => {
      moveListener.remove();
    };
  }, [active, onMouseMove, manager]);
};
